import React, { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Search, Star } from "lucide-react";

function ScoreGauge({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const stroke = 12;
  const r = 70;
  const c = 2 * Math.PI * r;
  const off = c - (s / 100) * c;
  const color = s >= 80 ? "#10B981" : s >= 60 ? "#BFFF3A" : s >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative w-[180px] h-[180px]">
      <svg viewBox="0 0 180 180" className="w-full h-full -rotate-90">
        <circle cx="90" cy="90" r={r} stroke="#27272A" strokeWidth={stroke} fill="none" />
        <circle cx="90" cy="90" r={r} stroke={color} strokeWidth={stroke} fill="none"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.3s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-5xl font-black h-tight text-white" data-testid="grader-score">{s}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">out of 100</div>
      </div>
    </div>
  );
}

export default function GraderWidget({ dark = true }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!title && !description) { toast.error("Paste a title or description first"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await http.post("/grade", {
        title, description,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grader failed");
    } finally { setLoading(false); }
  };

  const containerCls = dark
    ? "rounded-2xl border border-zinc-800 bg-zinc-950 p-6 md:p-8 text-zinc-100"
    : "tt-card p-6 md:p-8";
  const inputCls = dark
    ? "w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-lime focus:outline-none focus:ring-2 focus:ring-[#BFFF3A]/40"
    : "input";

  return (
    <div className={containerCls} data-testid="grader-widget">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.25em] text-lime">
          <Star size={12} /> Free Grader
        </span>
        <span className={dark ? "text-xs text-zinc-500" : "text-xs text-zinc-500"}>· no signup</span>
      </div>
      <h3 className={(dark ? "text-white" : "text-zinc-900") + " text-2xl md:text-3xl font-extrabold h-tight mb-1"}>
        Score any listing in 5 seconds.
      </h3>
      <p className={dark ? "text-zinc-400 text-sm mb-5" : "text-zinc-500 text-sm mb-5"}>
        Paste an Etsy, Amazon or Redbubble listing. Get a 0-100 SEO score plus specific fixes.
      </p>

      <div className="grid gap-3">
        <input
          data-testid="grader-title-input"
          className={inputCls} placeholder="Paste title…"
          value={title} onChange={e => setTitle(e.target.value)}
        />
        <textarea
          data-testid="grader-desc-input"
          className={inputCls + " min-h-[90px] resize-y"} placeholder="Paste description…"
          value={description} onChange={e => setDescription(e.target.value)}
        />
        <input
          data-testid="grader-tags-input"
          className={inputCls} placeholder="Paste tags, comma separated…"
          value={tags} onChange={e => setTags(e.target.value)}
        />
        <button
          data-testid="grader-submit-btn"
          disabled={loading} onClick={submit}
          className="btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
        >
          {loading ? "Scoring…" : (<><Search size={16} /> Grade my listing</>)}
        </button>
      </div>

      {result && (
        <div className="mt-7 grid md:grid-cols-[auto,1fr] gap-6 items-center tt-fade">
          <ScoreGauge score={result.score} />
          <div className="grid gap-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <ScoreLine label="Title keywords" value={result.titleScore} dark={dark} />
              <ScoreLine label="Tag quality" value={result.tagsScore} dark={dark} />
              <ScoreLine label="Description SEO" value={result.descriptionScore} dark={dark} />
              <ScoreLine label="Readability" value={result.readabilityScore} dark={dark} />
            </div>
            <div className="mt-3">
              <div className={"text-[10px] uppercase tracking-[0.25em] " + (dark ? "text-zinc-500" : "text-zinc-500")}>Top fixes</div>
              <ul className="mt-2 space-y-2" data-testid="grader-fixes">
                {(result.topFixes || []).slice(0,5).map((f, i) => (
                  <li key={i} className={"text-sm flex gap-2 " + (dark ? "text-zinc-200" : "text-zinc-800")}>
                    <Sparkles size={14} className="text-lime shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreLine({ label, value, dark }) {
  const v = value ?? 0;
  return (
    <div className={"flex items-center justify-between rounded-lg px-3 py-2 " + (dark ? "bg-zinc-900 border border-zinc-800" : "bg-zinc-50 border border-zinc-200")}>
      <span className={"text-xs uppercase tracking-wider " + (dark ? "text-zinc-400" : "text-zinc-500")}>{label}</span>
      <span className={"mono font-bold " + (dark ? "text-white" : "text-zinc-900")}>{v}</span>
    </div>
  );
}
